import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "@/utils/axiosInstance";
import { sendCustomGaEvent } from "@/scripts/ga";
import { buildAnalyticsFormDataProps } from "@/utils/analyticsFormData";
import { setLeadId } from "@/utils/sessionTracker";

export type LeadPlatform =
	| "chatbot"
	| "course_finder"
	| "global_search"
	| "manual_course_search"
	| "enquiry_form"
	| "facebook"
	| "instagram"
	| "google"
	| "whatsapp"
	| "meta"
	| "website"
	| "signup"
	| "instant_form"
	| "youtube"
	| "cross_platforms"
	| "blog_lead_card";

export type SaveLeadPayload = {
	phone: string;
	platform?: LeadPlatform;
	data?: Record<string, unknown>;
};

export type SaveLeadResponse = {
	status?: number;
	message?: string;
	leadId?: string;
};

export const submitLead = createAsyncThunk<
	SaveLeadResponse,
	SaveLeadPayload,
	{ rejectValue: string }
>("lead/create", async (payload, { rejectWithValue }) => {
	let dataParams: Record<string, any> = {};
	if (typeof window !== "undefined") {
		let storedParams: Record<string, string> = {};
		try {
			const sessionSaved = window.sessionStorage.getItem("edvia_utm_params");
			if (sessionSaved) {
				storedParams = JSON.parse(sessionSaved);
			} else {
				const localSaved = window.localStorage.getItem("edvia_utm_params");
				if (localSaved) {
					storedParams = JSON.parse(localSaved);
				}
			}
		} catch (e) {
			console.error("Failed to parse stored UTM params", e);
		}

		const searchParams = new URLSearchParams(window.location.search);
		searchParams.forEach((value, key) => {
			if (value) {
				storedParams[key] = value;
			}
		});

		dataParams = { ...storedParams };
		dataParams.pageUrl = window.location.href;
		if (document.referrer) {
			dataParams.referrerUrl = document.referrer;
		}
	}

	const enrichedPayload = {
		...payload,
		data: {
			...(payload.data || {}),
			...dataParams,
		},
	};

	try {
		const analyticsPayload = {
			platform: enrichedPayload.platform || "website",
			source:
				typeof enrichedPayload.data?.source === "string"
					? enrichedPayload.data.source
					: "unknown",
			...buildAnalyticsFormDataProps({
				phone: enrichedPayload.phone,
				...(enrichedPayload.data || {}),
			}),
		};

		await sendCustomGaEvent("lead_submit_attempt", {
			...analyticsPayload,
		});

		const { data } = await axiosInstance.post("/leads", enrichedPayload);

		await sendCustomGaEvent("lead_submit_success", {
			...analyticsPayload,
		});

		if (data?.leadId) {
			setLeadId(data.leadId);
		}

		return data as SaveLeadResponse;
	} catch (error: any) {
		const analyticsPayloadError = {
			platform: enrichedPayload.platform || "website",
			source:
				typeof enrichedPayload.data?.source === "string"
					? enrichedPayload.data.source
					: "unknown",
			...buildAnalyticsFormDataProps({
				phone: enrichedPayload.phone,
				...(enrichedPayload.data || {}),
			}),
			error_message:
				error?.response?.data?.message || error?.message || "unknown_error",
		};

		await sendCustomGaEvent("lead_submit_failed", analyticsPayloadError);

		const message =
			error?.response?.data?.message || "Failed to capture lead.";
		return rejectWithValue(message);
	}
});


// Registered student: the backend reads the shortlisted courses from the student's profile
export const sendRegisteredRecommendations = (studentId: string, payload?: any) =>
	axiosInstance.post(`counsellor/send-recommendations/${studentId}`, payload || {}, {
		apiVersion: "v2",
	});
